/**
 * Settings, branding, the InBody toggle, and users.
 *
 * This screen is where white-labelling actually happens (§0.1): every field maps
 * to a tenant column that the report template reads on the next render.
 *
 * Simplified per Brian's 23 September redesign brief: no hero-image picker, no
 * Brand identity card (so no logo uploader), no Integrations card. The clinic
 * still has a stored theme underneath; there is simply no clinic-facing screen
 * left that edits the logo or hero image, or that surfaces provider status.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BrandTheme } from '@mcos/types';
import { ClinicWordingCard } from './PlanQuestions';
import { AwayNoticeCard } from '../components/AwayNotice';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime } from '../lib/format';
import { PublicListingCard } from '../components/PublicListingCard';
import {
  Button,
  Card,
  ErrorState,
  Field,
  LoadingRows,
  PageHeader,
  Pill,
  Table,
  Td,
  Th,
  Toggle,
  inputClass,
} from '../components/ui';

interface UserRow {
  id: string;
  email: string;
  name: string;
  role: 'ADMIN' | 'CLINICIAN' | 'STAFF';
  active: boolean;
  lastLoginAt: string | null;
}

export function SettingsPage() {
  const queryClient = useQueryClient();
  const { refreshUser } = useAuth();

  const branding = useQuery({
    queryKey: ['branding'],
    queryFn: () => api.get<{ theme: BrandTheme }>('/settings/branding'),
  });

  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<{ data: UserRow[] }>('/settings/users'),
  });

  const [draft, setDraft] = useState<Partial<BrandTheme>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset the draft whenever the saved theme changes.
  useEffect(() => {
    if (branding.data) setDraft({});
  }, [branding.data]);

  const theme = branding.data?.theme;
  const merged = { ...theme, ...draft } as BrandTheme;

  const saveMutation = useMutation({
    mutationFn: () =>
      api.patch<{ theme: BrandTheme }>('/settings/branding', {
        // Only send what actually changed, so a partial edit cannot blank a
        // field the user never touched.
        name: draft.name,
        wordmark: draft.wordmark,
        wordmarkSub: draft.wordmark_sub,
        brandWord: draft.brand_word,
        primaryColor: draft.primary_color,
        accentColor: draft.accent_color,
        neutralBg: draft.neutral_bg,
        inkColor: draft.ink_color,
        reportTitle: draft.report_title,
        reportTagline: draft.report_tagline,
        stripSlogan: draft.strip_slogan,
        stripSloganSub: draft.strip_slogan_sub,
        footerText: draft.footer_text,
        disclaimerText: draft.disclaimer_text,
        ctaTitle: draft.cta_title,
        ctaBody: draft.cta_body,
        ctaQrUrl: draft.cta_qr_url,
        scoreColorOptimal: draft.score_color_optimal,
        scoreColorNeedsWork: draft.score_color_needs_work,
        scoreColorHighRisk: draft.score_color_high_risk,
        inbodyEnabled: draft.inbody_enabled,
        testLabelEnabled: draft.test_label_enabled,
        showMcosMark: draft.show_mcos_mark,
      }),
    onSuccess: () => {
      setError(null);
      setMessage('Saved. The next generated report will use these settings.');
      void queryClient.invalidateQueries({ queryKey: ['branding'] });
      // Re-read the session so the sidebar picks up a wordmark or colour change.
      void refreshUser();
    },
    onError: (caught) => {
      setMessage(null);
      setError(caught instanceof Error ? caught.message : 'Could not save');
    },
  });

  if (branding.error) {
    return <ErrorState error={branding.error} retry={() => void branding.refetch()} />;
  }
  if (branding.isLoading || !theme) return <LoadingRows rows={10} />;

  const patch = (changes: Partial<BrandTheme>) => setDraft({ ...draft, ...changes });
  const dirty = Object.keys(draft).length > 0;

  return (
    <>
      <PageHeader
        title="Settings"
        subtitle="Features, report colours and copy, and users for this clinic."
        actions={
          <Button
            loading={saveMutation.isPending}
            disabled={!dirty}
            onClick={() => saveMutation.mutate()}
          >
            Save changes
          </Button>
        }
      />

      {(message || error) && (
        <div
          className={`mb-5 rounded-md2 px-4 py-3 text-sm ${
            error
              ? 'border border-red-200 bg-red-50 text-red-800'
              : 'border border-emerald-200 bg-emerald-50 text-emerald-800'
          }`}
        >
          {error ?? message}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* --- Features ------------------------------------------------ */}
        <Card title="Features">
          <div className="divide-y divide-slate-100">
            <Toggle
              checked={merged.inbody_enabled}
              onChange={(next) => patch({ inbody_enabled: next })}
              label="Body composition (InBody)"
              description="Adds InBody capture to the app and the 'Your Body Composition' section to reports. When off, the section is omitted entirely, no empty box."
            />
            <Toggle
              checked={merged.test_label_enabled}
              onChange={(next) => patch({ test_label_enabled: next })}
              label="Test-patient label"
              description='Prints "TEST PATIENT / NOT FOR CLINICAL USE" on every report. Keep it on while you try MCOS with sample patients, and turn it off before you report on real patients.'
            />
            <Toggle
              checked={merged.show_mcos_mark}
              onChange={(next) => patch({ show_mcos_mark: next })}
              label="Show the MCOS mark"
              description="Prints the MCOS™ wordmark beside your own logo on the report header. MCOS is the software and your clinic is the brand. Turn it off for a report that carries only your clinic's brand."
            />
          </div>
        </Card>

        {/* --- Colours ------------------------------------------------- */}
        <Card title="Colours">
          <div className="grid grid-cols-2 gap-4">
            <ColorField label="Primary" value={merged.primary_color}
              onChange={(v) => patch({ primary_color: v })} />
            <ColorField label="Accent" value={merged.accent_color}
              onChange={(v) => patch({ accent_color: v })} />
            <ColorField label="Page background" value={merged.neutral_bg}
              onChange={(v) => patch({ neutral_bg: v })} />
            <ColorField label="Ink" value={merged.ink_color}
              onChange={(v) => patch({ ink_color: v })} />
          </div>

          <div className="mt-5 pt-4 border-t border-ink/5">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted mb-3">
              Score bands
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <ColorField label="80-100" value={merged.score_color_optimal}
                onChange={(v) => patch({ score_color_optimal: v })} />
              <ColorField label="60-79" value={merged.score_color_needs_work}
                onChange={(v) => patch({ score_color_needs_work: v })} />
              <ColorField label="0-59" value={merged.score_color_high_risk}
                onChange={(v) => patch({ score_color_high_risk: v })} />
            </div>
          </div>
        </Card>

        {/* --- Report copy --------------------------------------------- */}
        <Card title="Report copy" className="xl:col-span-2">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Field label="Report title">
              <input className={inputClass} value={merged.report_title}
                onChange={(e) => patch({ report_title: e.target.value })} />
            </Field>
            <Field label="Tagline">
              <input className={inputClass} value={merged.report_tagline ?? ''}
                onChange={(e) => patch({ report_tagline: e.target.value || null })} />
            </Field>
            <Field label="Patient-strip slogan">
              <input className={inputClass} value={merged.strip_slogan ?? ''}
                onChange={(e) => patch({ strip_slogan: e.target.value || null })} />
            </Field>
            <Field label="Patient-strip slogan, line 2">
              <input className={inputClass} value={merged.strip_slogan_sub ?? ''}
                onChange={(e) => patch({ strip_slogan_sub: e.target.value || null })} />
            </Field>
            <Field label="Call-to-action title">
              <input className={inputClass} value={merged.cta_title ?? ''}
                onChange={(e) => patch({ cta_title: e.target.value || null })} />
            </Field>
            <Field label="Call-to-action body">
              <input className={inputClass} value={merged.cta_body ?? ''}
                onChange={(e) => patch({ cta_body: e.target.value || null })} />
            </Field>
            <Field label="Booking URL" hint="Encoded into the footer QR code">
              <input className={inputClass} value={merged.cta_qr_url ?? ''}
                onChange={(e) => patch({ cta_qr_url: e.target.value || null })} />
            </Field>
            <Field label="Footer text">
              <input className={inputClass} value={merged.footer_text}
                onChange={(e) => patch({ footer_text: e.target.value })} />
            </Field>
            <div className="lg:col-span-2">
              <Field
                label="Disclaimer"
                hint="Printed on every report. Required for compliance, it cannot be removed, only reworded."
              >
                <textarea rows={4} className={inputClass} value={merged.disclaimer_text}
                  onChange={(e) => patch({ disclaimer_text: e.target.value })} />
              </Field>
            </div>
          </div>
        </Card>

        {/* --- Public listing (the provider directory) ------------------- */}
        <div className="xl:col-span-2">
          <PublicListingCard />
        </div>

        {/* --- Users --------------------------------------------------- */}
        <AwayNoticeCard className="xl:col-span-2" />
        <ClinicWordingCard className="xl:col-span-2" />
        <Card title="Users" padded={false} className="xl:col-span-2">
          {users.isLoading ? (
            <div className="p-5"><LoadingRows rows={4} /></div>
          ) : users.data ? (
            <Table>
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th align="center">Role</Th>
                  <Th align="center">Status</Th>
                  <Th align="right">Last sign-in</Th>
                </tr>
              </thead>
              <tbody>
                {users.data.data.map((user) => (
                  <tr key={user.id} className="hover:bg-ink/5/70">
                    <Td className="font-medium text-ink max-sm:whitespace-nowrap">{user.name}</Td>
                    <Td className="text-ink-muted text-xs">{user.email}</Td>
                    <Td align="center">
                      <Pill
                        className={
                          user.role === 'ADMIN'
                            ? 'bg-indigo-50 text-indigo-700 ring-indigo-200'
                            : user.role === 'CLINICIAN'
                              ? 'bg-blue-50 text-blue-700 ring-blue-200'
                              : 'bg-ink/5 text-ink-muted ring-ink/10'
                        }
                      >
                        {user.role.toLowerCase()}
                      </Pill>
                    </Td>
                    <Td align="center">
                      <Pill
                        className={
                          user.active
                            ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                            : 'bg-red-50 text-red-700 ring-red-200'
                        }
                      >
                        {user.active ? 'active' : 'disabled'}
                      </Pill>
                    </Td>
                    <Td align="right" className="text-xs text-ink-muted">
                      {user.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'never'}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}
        </Card>
      </div>
    </>
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <Field label={label}>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          aria-label={`${label} colour picker`}
          onChange={(event) => onChange(event.target.value.toUpperCase())}
          className="h-9 w-9 rounded border border-ink/10 cursor-pointer bg-white p-0.5"
        />
        <input
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value.toUpperCase())}
          className={`${inputClass} font-mono text-xs`}
        />
      </div>
    </Field>
  );
}

